import { useEffect } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";

import { ScheduleStage } from "@/api/schedule/models";
import { UserRoleEnum } from "@/api/user/models";
import { Tabs } from "@/components/common/Tabs";
import { ScheduleDetailAside } from "@/components/features/schedule/ScheduleDetailAside";
import {
  SCHEDULE_DETAIL_NAVIGATION_ITEMS,
  SCHEDULE_STAGE_LABEL_BY_STAGE,
  SCHEDULE_STAGE_MANAGEMENT_ALLOWED_ROLES,
} from "@/constants/schedule";
import { useDepartmentLink } from "@/hooks/common/use-department-link";
import { useDepartmentRoute } from "@/hooks/common/use-department-route";
import { useScheduleDetails } from "@/hooks/features/schedule/use-schedule-details";
import { buildRoute, RoutePathEnum } from "@/routes/paths";
import { useBreadcrumbStore } from "@/stores/use-breadcrumb-store";
import { useUserStore } from "@/stores/use-user-store";
import { getAllowedNavigationItems } from "@/utils/features/schedule/navigation";
import { getUserRoleByDepartment, userHasRoleInDepartment } from "@/utils/user-positions";
import { formatDateInSaoPauloTimeZone } from "@/utils/date";

const SCHEDULE_STAGE_TABS = (["RECEPTION", "PHOTOGRAPHY", "PRODUCTION", "ART_PRODUCTION"] as ScheduleStage[]).map(
  (stage) => {
    return { value: stage, label: SCHEDULE_STAGE_LABEL_BY_STAGE[stage] };
  },
);

export const ScheduleDetailLayout = () => {
  const { pathname } = useLocation();
  const { scheduleId } = useParams<{ scheduleId: string }>();
  const buildDepartmentLink = useDepartmentLink();
  const { department } = useDepartmentRoute();
  const activeUser = useUserStore((state) => state.activeUser);
  const scheduleDetail = useScheduleDetails();
  const setOverride = useBreadcrumbStore((state) => state.setOverride);
  const removeOverride = useBreadcrumbStore((state) => state.removeOverride);

  useEffect(() => {
    if (!scheduleDetail.data || !scheduleId) {
      return;
    }

    setOverride("/[department]/agenda/[scheduleId]", {
      label: scheduleDetail.data.participant.fullName,
      path: buildDepartmentLink(buildRoute(RoutePathEnum.SCHEDULE_TALENT_DATA, { scheduleId })),
    });

    return () => {
      removeOverride("/[department]/agenda/[scheduleId]");
    };
  }, [buildDepartmentLink, removeOverride, scheduleDetail.data, scheduleId, setOverride]);

  const productionRoles = [UserRoleEnum.ADMINISTRATOR, UserRoleEnum.COORDINATOR, UserRoleEnum.RECEPTIONIST];
  const isEligibleProducer =
    getUserRoleByDepartment(activeUser, department) === UserRoleEnum.PRODUCER &&
    Boolean(activeUser?.id) &&
    scheduleDetail.data?.department === department &&
    scheduleDetail.data?.responsibleProducerId === activeUser?.id &&
    scheduleDetail.data?.currentStage === "PRODUCTION" &&
    scheduleDetail.data.status === "IN_PROGRESS" &&
    scheduleDetail.data.scheduleDate?.slice(0, 10) === formatDateInSaoPauloTimeZone();
  const canViewProduction = userHasRoleInDepartment(activeUser, department, productionRoles) || isEligibleProducer;

  const canViewContracts =
    scheduleDetail.data?.isPresent === true &&
    !userHasRoleInDepartment(activeUser, department, [UserRoleEnum.PHOTOGRAPHER, UserRoleEnum.ART_PRODUCER]);

  const navigationItems = getAllowedNavigationItems(scheduleDetail.data?.scheduleType).filter((item) => {
    return (
      (item.path !== RoutePathEnum.SCHEDULE_PRODUCTION || canViewProduction) &&
      (item.path !== RoutePathEnum.SCHEDULE_CONTRACTS || canViewContracts)
    );
  });

  const restrictedNavigationItems = SCHEDULE_DETAIL_NAVIGATION_ITEMS.filter((item) => !navigationItems.includes(item));

  const isRestrictedPath = restrictedNavigationItems.some(({ path }) => {
    return buildDepartmentLink(buildRoute(path, { scheduleId: scheduleId ?? "" })) === pathname.replace(/\/+$/, "");
  });

  const visibleStage = scheduleDetail.data?.isPresent ? scheduleDetail.data.currentStage : undefined;

  const canViewScheduleStages = userHasRoleInDepartment(
    activeUser,
    department,
    SCHEDULE_STAGE_MANAGEMENT_ALLOWED_ROLES,
  );

  const isDisplayedStage = SCHEDULE_STAGE_TABS.some(({ value }) => value === visibleStage);

  if (scheduleDetail.errorStatus === 403 || (scheduleDetail.data && isRestrictedPath)) {
    return <Navigate replace to={buildDepartmentLink(RoutePathEnum.SCHEDULE)} />;
  }

  return (
    <div className="flex h-full min-w-0 flex-col gap-4 md:flex-row md:gap-8">
      <ScheduleDetailAside
        isLoading={scheduleDetail.isPendingOrErrored}
        navigationItems={navigationItems}
        schedule={scheduleDetail.data}
      />
      <main className="flex-1 flex flex-col gap-6">
        {canViewScheduleStages && isDisplayedStage && (
          <Tabs tabs={SCHEDULE_STAGE_TABS} activeTab={visibleStage ?? ""} />
        )}
        <Outlet />
      </main>
    </div>
  );
};
